/**
 * Cadastro e gestão do próprio segundo fator. (BKC-012)
 *
 * UMA tela para dois estados, porque a pergunta da pessoa é a mesma nos dois
 * casos ("como está meu segundo fator?"):
 *
 *   - NÃO cadastrado: gera o segredo, mostra a chave para digitar no aplicativo
 *     e pede um código para confirmar. Só depois da confirmação o fator liga.
 *   - JÁ cadastrado: mostra a situação, permite renovar os códigos de recuperação
 *     e, quando o perfil NÃO exige o fator, removê-lo com a senha.
 *
 * COM QR CODE, gerado no navegador. A primeira versão desta tela só oferecia a
 * chave para digitação, e eu justifiquei isso com "economizar uma digitação". Era
 * a ponderação errada: são 32 caracteres em base32, digitados num teclado de
 * celular, por quem está tentando ativar segurança — o tipo de atrito que faz a
 * pessoa desistir ou errar e culpar o sistema.
 *
 * O QR é montado AQUI, como SVG inline, a partir da URI que já está em memória.
 * Nenhum serviço externo de geração é usado: ele receberia o segredo do segundo
 * fator em texto claro, no log de acesso de terceiro.
 *
 * A digitação manual continua disponível, recolhida: é o caminho de quem cadastra
 * num aplicativo de computador ou gerenciador de senhas, onde não há câmera.
 *
 * Os códigos de recuperação aparecem UMA vez. A tela diz isso antes e exige
 * confirmação explícita de que foram guardados.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { useSession } from '../lib/session';
import {
  Field,
  FormErrorSummary,
  InlineError,
  LoadingRows,
  PageHeader,
  Spinner,
  useToast,
} from '../components/ui';
import { HelpLink } from '../components/help';
import { QrCode } from '../components/QrCode';
import { formatDateTime } from '../lib/format';

interface MfaStatus {
  enabled: boolean;
  required: boolean;
  setupRequired: boolean;
  confirmedAt: string | null;
  recoveryCodesRemaining: number;
  recoveryCodesLow: boolean;
  available: boolean;
}

interface Enrollment {
  secret: string;
  secretFormatted: string;
  uri: string;
}

/** Lista de códigos de recuperação, exibida uma única vez. */
function CodigosDeRecuperacao({
  codigos,
  onConfirmar,
}: {
  codigos: string[];
  onConfirmar: () => void;
}): JSX.Element {
  const { notify } = useToast();
  const [guardou, setGuardou] = useState(false);

  return (
    <section className="panel" data-testid="mfa-recovery-codes">
      <div className="panel-header">
        <h2 className="panel-title">{t('mfa.guardeCodigos')}</h2>
      </div>
      <div className="space-y-3 px-4 py-3">
        <p className="rounded border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-700">
          Esta é a <strong>única vez</strong>{t('mfa.versaoIrreversivel')}</p>

        <ul className="grid grid-cols-2 gap-1.5 rounded border border-line bg-surface-sunken p-3 font-mono text-sm tabular-nums">
          {codigos.map((codigo) => (
            <li key={codigo} className="tracking-wider text-ink">
              {codigo}
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(codigos.join('\n'))
                .then(() => notify('success', 'Códigos copiados.'))
                .catch(() => notify('error', 'Não foi possível copiar. Anote os códigos.'));
            }}
          >{t('mfa.copiarTodos')}</button>
          <button type="button" className="btn-secondary btn-sm" onClick={() => window.print()}>{t('mfa.imprimir')}</button>
        </div>

        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-line-strong"
            data-testid="mfa-guardou"
            checked={guardou}
            onChange={(event) => setGuardou(event.target.checked)}
          />{t('mfa.guardeiCodigos')}</label>

        <button
          type="button"
          className="btn-primary"
          data-testid="mfa-concluir"
          disabled={!guardou}
          onClick={onConfirmar}
        >{t('mfa.concluir')}</button>
      </div>
    </section>
  );
}

export function MfaSetupPage(): JSX.Element {
  const { user, refresh } = useSession();
  const { notify } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [codigos, setCodigos] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [senha, setSenha] = useState('');
  const [removendo, setRemovendo] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const status = useQuery({
    queryKey: ['mfa'],
    queryFn: () => api.get<{ mfa: MfaStatus }>('/auth/mfa'),
  });

  const iniciar = useMutation({
    mutationFn: () => api.post<{ enrollment: Enrollment }>('/auth/mfa/enroll'),
    onSuccess: (body) => {
      setEnrollment(body.enrollment);
      setError(null);
    },
    onError: setError,
  });

  const confirmar = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/auth/mfa/confirm', { code }),
    onSuccess: async (body) => {
      setCodigos(body.recoveryCodes);
      setEnrollment(null);
      setCode('');
      setError(null);
      // A sessão sai do modo restrito na hora: recarregar aqui evita que a
      // pessoa veja uma tela de acesso negado logo depois de acertar o código.
      await refresh();
      void queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
    onError: setError,
  });

  const renovarCodigos = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', { code }),
    onSuccess: (body) => {
      setCodigos(body.recoveryCodes);
      setCode('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
    onError: setError,
  });

  const remover = useMutation({
    mutationFn: () => api.delete('/auth/mfa', { password: senha }),
    onSuccess: async () => {
      notify('success', 'Segundo fator removido.');
      setSenha('');
      setRemovendo(false);
      setError(null);
      await refresh();
      void queryClient.invalidateQueries({ queryKey: ['mfa'] });
    },
    onError: setError,
  });

  if (status.isPending) {
    return (
      <>
        <PageHeader title={t('mfa.titulo')} />
        <div className="panel">
          <LoadingRows rows={3} columns={1} />
        </div>
      </>
    );
  }

  const dados = status.data?.mfa;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title={t('mfa.titulo')}
        subtitle={
          dados?.required === true
            ? 'Seu perfil administrativo exige um segundo fator: senha sozinha não basta para configurar regras, mexer em credenciais alheias e ler a auditoria inteira.'
            : 'Um código do seu celular, além da senha. Opcional para o seu perfil, e recomendado.'
        }
        help={<HelpLink point="seguranca.segundo-fator" />}
      />

      {dados?.available === false && (
        <div className="panel mb-3 px-4 py-3" role="alert">
          <p className="text-sm text-danger-700">{t('mfa.naoConfigurado')}</p>
        </div>
      )}

      {codigos !== null ? (
        <CodigosDeRecuperacao
          codigos={codigos}
          onConfirmar={() => {
            setCodigos(null);
            navigate('/', { replace: true });
          }}
        />
      ) : enrollment !== null ? (
        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">{t('mfa.cadastreApp')}</h2>
          </div>
          <div className="space-y-4 px-4 py-3">
            <ol className="list-decimal space-y-2 pl-5 text-sm text-ink">
              <li>{t('mfa.instaleApp')}</li>
              <li>
                No aplicativo, escolha <strong>adicionar conta</strong> e depois{' '}
                <strong>escanear código QR</strong>.
              </li>
              <li>{t('mfa.aponteCamera')}</li>
            </ol>

            <div className="flex flex-col items-center gap-2 rounded border border-line bg-surface p-4 sm:flex-row sm:items-start sm:gap-5">
              {/* O QR é gerado AQUI, no navegador, a partir da URI que já está em
                  memória. Nenhum serviço externo de geração é usado: ele receberia
                  o segredo do segundo fator em texto claro, no log de acesso de
                  terceiro. */}
              <QrCode
                value={enrollment.uri}
                label={t('mfa.qrLabel')}
              />

              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink">{t('mfa.escaneie')}</p>

                {/* A digitação continua disponível, e é o caminho de quem cadastra
                    num aplicativo de computador ou num gerenciador de senhas, onde
                    não há câmera. Escondê-la atrás de um clique custaria pouco na
                    tela e muito para quem precisa dela. */}
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium text-brand-700 hover:underline">{t('mfa.naoConsegueEscanear')}</summary>
                  <div className="mt-2">
                    <p className="field-label">{t('mfa.chave')}</p>
                    <p
                      className="select-all break-all rounded border border-line bg-surface-sunken px-3 py-2 font-mono text-base tracking-widest text-ink"
                      data-testid="mfa-secret"
                    >
                      {enrollment.secretFormatted}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          void navigator.clipboard
                            ?.writeText(enrollment.secret)
                            .then(() => notify('success', 'Chave copiada.'))
                            .catch(() => notify('error', 'Não foi possível copiar.'));
                        }}
                      >{t('mfa.copiarChave')}</button>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          void navigator.clipboard
                            ?.writeText(enrollment.uri)
                            .then(() => notify('success', 'Endereço de configuração copiado.'))
                            .catch(() => notify('error', 'Não foi possível copiar.'));
                        }}
                      >{t('mfa.copiarOtpauth')}</button>
                    </div>
                    <p className="mt-1 text-xs text-ink-subtle">
                      No aplicativo: <strong>inserir chave manualmente</strong>. Tipo: baseado em
                      tempo (TOTP) · 6 dígitos · troca a cada 30 segundos.
                    </p>
                  </div>
                </details>
              </div>
            </div>

            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                confirmar.mutate();
              }}
            >
              <Field
                label={t('mfa.codigoApp')}
                htmlFor="mfa-confirm-code"
                required
                hint="Se o código for recusado, confira se a chave foi digitada por completo e se a hora do celular está automática."
              >
                <input
                  id="mfa-confirm-code"
                  data-testid="mfa-confirm-code"
                  className="field-input text-center text-lg tracking-[0.3em] tabular-nums"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={7}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </Field>

              <FormErrorSummary error={error} />
              <InlineError error={error} />

              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="mfa-confirmar"
                  disabled={confirmar.isPending || code.trim().length < 6}
                >
                  {confirmar.isPending && <Spinner />}
                  Ativar segundo fator
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setEnrollment(null);
                    setCode('');
                    setError(null);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </section>
      ) : dados?.enabled === true ? (
        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">{t('mfa.segundoFatorAtivo')}</h2>
            <span className="badge-success">ativo</span>
          </div>
          <div className="space-y-4 px-4 py-3">
            <p className="text-sm text-ink-muted">
              Ativado em{' '}
              {dados.confirmedAt === null
                ? '—'
                : formatDateTime(dados.confirmedAt)}
              . Códigos de recuperação disponíveis:{' '}
              <strong className="tabular-nums">{dados.recoveryCodesRemaining}</strong>.
            </p>

            {dados.recoveryCodesLow && (
              <p className="rounded border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-700">{t('mfa.restamPoucos')}</p>
            )}

            <form
              className="space-y-2 border-t border-line pt-3"
              onSubmit={(event) => {
                event.preventDefault();
                renovarCodigos.mutate();
              }}
            >
              <Field
                label={t('mfa.renovarCodigos')}
                htmlFor="mfa-renew-code"
                hint="Informe um código do aplicativo. A lista anterior deixa de valer imediatamente."
              >
                <input
                  id="mfa-renew-code"
                  className="field-input sm:w-40 text-center tracking-[0.3em] tabular-nums"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={7}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </Field>
              <button
                type="submit"
                className="btn-secondary"
                disabled={renovarCodigos.isPending || code.trim().length < 6}
              >
                {renovarCodigos.isPending && <Spinner />}
                Gerar lista nova
              </button>
            </form>

            <div className="border-t border-line pt-3">
              {dados.required ? (
                <p className="text-sm text-ink-muted">
                  Seu perfil <strong>exige</strong>{t('mfa.naoPodeRemover')}</p>
              ) : removendo ? (
                <form
                  className="space-y-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    remover.mutate();
                  }}
                >
                  <Field
                    label={t('mfa.confirmeSenha')}
                    htmlFor="mfa-senha"
                    required
                  >
                    <input
                      id="mfa-senha"
                      type="password"
                      className="field-input sm:w-72"
                      autoComplete="current-password"
                      value={senha}
                      onChange={(event) => setSenha(event.target.value)}
                    />
                  </Field>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="submit"
                      className="btn-danger"
                      disabled={remover.isPending || senha === ''}
                    >
                      {remover.isPending && <Spinner />}
                      Remover segundo fator
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => {
                        setRemovendo(false);
                        setSenha('');
                        setError(null);
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setRemovendo(true);
                    setError(null);
                  }}
                >{t('mfa.removerSegundoFator')}</button>
              )}
            </div>

            <FormErrorSummary error={error} />
            <InlineError error={error} />
          </div>
        </section>
      ) : (
        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">{t('mfa.aindaNaoCadastrado')}</h2>
            {dados?.required === true && <span className="badge-warning">exigido</span>}
          </div>
          <div className="space-y-3 px-4 py-3">
            <p className="text-sm text-ink-muted">{t('mfa.duasEtapas')}</p>

            {user?.mfaSetupRequired === true && (
              <p className="rounded border border-line bg-surface-sunken px-3 py-2 text-sm text-ink-muted">{t('mfa.acessoLimitado')}</p>
            )}

            <FormErrorSummary error={error} />
            <InlineError error={error} />

            <button
              type="button"
              className="btn-primary"
              data-testid="mfa-iniciar"
              disabled={iniciar.isPending || dados?.available === false}
              onClick={() => iniciar.mutate()}
            >
              {iniciar.isPending && <Spinner />}
              Começar o cadastro
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
